import { useToast } from '@/components/ui/use-toast';
import {
  CourseEntity,
  LearnEntity,
  CreateCourseFeedbackMutation,
  CreateCourseFeedbackMutationVariables,
  CourseFeedbackPageQuery,
} from '@/graphql/generated/graphql';
import { MUTATION_CREATE_COURSE_FEEDBACK } from '@/graphql/mutations/learn/learn';
import { QUERY_COURSE_FEEDBACK_PAGE } from '@/graphql/queries/learn/page';
import { useAuthentication } from '@/hooks/useAuthentication';
import { trackCourseCompleted } from '@/lib/ga-custom-events/course';
import { fetchCourseLessonBySlug } from '@/services/learn/course.service';
import { fetchCourseLearn, completeLearn } from '@/services/learn/learn.service';
import { useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import { z } from 'zod';

const feedbackSchema = z.object({
  answers: z.array(
    z.object({
      question: z.string().min(1),
      answer: z.string().min(1, { message: 'Please provide an answer' }),
    }),
  ),
});

type FeedbackFormData = z.infer<typeof feedbackSchema>;

export const useFeedback = () => {
  const [course, setCourse] = useState<CourseEntity | null>(null);
  const [learn, setLearn] = useState<LearnEntity | null>(null);
  const [, setFormDirty] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const { slug } = useParams();
  const { authentication } = useAuthentication();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [createCourseFeedback, { loading, data: submissionData }] = useMutation<
    CreateCourseFeedbackMutation,
    CreateCourseFeedbackMutationVariables
  >(MUTATION_CREATE_COURSE_FEEDBACK);

  const { data: pageData } = useQuery<CourseFeedbackPageQuery>(QUERY_COURSE_FEEDBACK_PAGE);
  const page = pageData?.learnPage?.data?.attributes?.feedbackSection;

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
  } = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      answers:
        course?.attributes?.feedbackQuestions?.map((feedbackQuestion) => ({
          question: feedbackQuestion?.question || '',
          answer: '',
        })) || [],
    },
  });

  const items = [
    {
      label: 'Learn',
      url: '/learn',
    },
    {
      label: 'Courses',
      url: '/learn',
    },
    { label: course?.attributes?.title, url: `/learn/course/${course?.attributes?.slug}` },
    { label: 'Feedback' },
  ];

  // Custom navigation handler
  const handleNavigation = async (path: string) => {
    const valid = await trigger();
    if (!valid) {
      setPendingNavigation(path);
      setShowLeaveDialog(true);
    } else {
      navigate(path);
    }
  };

  // Handle navigation confirmation
  const handleConfirmNavigation = () => {
    setFormDirty(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
    setShowLeaveDialog(false);
  };

  const onSubmit = async (data: FeedbackFormData) => {
    try {
      await Promise.all([
        createCourseFeedback({
          variables: {
            data: {
              user: authentication.user.id,
              course: course?.id,
              submittedDate: new Date().toISOString(),
              answers: data.answers.map((a) => ({ question: a.question, answer: a.answer })),
            },
          },
        }),
        markCourseAsCompleted(),
      ]);

      toast({
        title: 'Success',
        description: 'Your feedback was submitted successfully',
      });
      setFormDirty(false);

      toast({
        description:
          pageData?.learnPage?.data?.attributes?.markCourseAsCompleted?.completedLabel ||
          'You have finished this course!',
        variant: 'default',
      });

      navigate(`/learn/course/${course?.attributes?.slug}`);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast({
        title: 'Error submitting feedback',
        description: error.message,
        variant: 'destructive',
      });
      console.error('Error submitting feedback:', error);
    }
  };

  const markCourseAsCompleted = async () => {
    if (!learn?.attributes?.completed) {
      await completeLearn(learn!.id!);
    }

    trackCourseCompleted(course as CourseEntity);
  };

  // Combined form state and navigation handling
  useEffect(() => {
    const handleBeforeUnload = async (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Handle submission and course data
  useEffect(() => {
    if (submissionData) {
      setFormDirty(false);
    }
  }, [submissionData]);

  // Load course data
  useEffect(() => {
    const loadCourseData = async () => {
      if (!slug) return;
      try {
        const [courseResult, learnResult] = await Promise.all([fetchCourseLessonBySlug(slug), fetchCourseLearn(slug)]);
        setCourse(courseResult);
        setLearn(learnResult);
      } catch (error) {
        console.error('Error loading course data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load course data',
          variant: 'destructive',
        });
      }
    };

    loadCourseData();
  }, [slug, toast]);

  return {
    course,
    page,
    items,
    showLeaveDialog,
    setShowLeaveDialog,
    handleConfirmNavigation,
    handleNavigation,
    onSubmit,
    handleSubmit,
    errors,
    loading,
    register,
    pageData,
  };
};
